import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

export const AboutSection = () => {
  const { t, isRTL } = useLanguage();

  const stats = [
    { value: '5+', label: t.about.years },
    { value: '1000+', label: t.about.clients },
    { value: '10+', label: t.about.experts },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="section-padding bg-white relative overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="decorative-blob blob-1" />
      
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1708459688387-403434955f3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Sirena Beauty Salon Interior"
                className="w-full h-full object-cover"
              />
              {/* Overlay decoration */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#D4A39A]/20 to-transparent" />
            </div>
            {/* Floating decoration */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
              className={`absolute -bottom-6 ${isRTL ? '-left-6' : '-right-6'} w-32 h-32 bg-[#D4A39A] rounded-2xl -z-10`}
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="text-[#D4A39A] text-sm font-semibold uppercase tracking-widest">
              {t.about.subtitle}
            </span>
            <h2 
              data-testid="about-title"
              className="heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#4A4A4A] mt-3 mb-6"
            >
              {t.about.title}
            </h2>
            
            <div className="space-y-4 text-[#7D7D7D] leading-relaxed">
              <p>{t.about.description1}</p>
              <p>{t.about.description2}</p>
              <p>{t.about.description3}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-10">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl md:text-4xl font-bold text-[#D4A39A] heading">
                    {stat.value}
                  </div>
                  <div className="text-sm text-[#7D7D7D] mt-1">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
